import React, { useContext } from 'react';
import {
  ButtonGroup,
  type LayoutProps,
  type TextButtonProps,
} from '@oneaudi/unified-web-components';
import {
  type ClickHandler,
  CtaCallbackButton,
  type CtaCallbackButtonProps,
} from './CtaCallbackButton';
import { CtaUrlButton, type CtaUrlButtonProps } from './CtaUrlButton';
import FeatureServiceContext from '../FeatureServiceContext';

export type CtaBarButtonConfig = {
  label: string;
  variant: TextButtonProps['variant'];
  url?: string;
  target?: '_self' | '_blank';
  clickHandler?: ClickHandler;
}[];

export type CtaBarProps = LayoutProps & {
  ctaBarButtonConfig: CtaBarButtonConfig;
  displayname: string;
};

export type CtaButtonProps = {
  displayname: string;
  label: string;
  position: string;
  variant: TextButtonProps['variant'];
  testId?: string;
};

export const CtaBar = ({ ctaBarButtonConfig, displayname }: CtaBarProps): React.ReactElement => {
  const logger = useContext(FeatureServiceContext).logger;
  // eslint-disable-next-line
  const [ctaButtons, setCtaButtons] = React.useState<any>();

  React.useEffect(() => {
    const labelOccurrences = new Map<string, number>();
    const ctaBtns = ctaBarButtonConfig.map((buttonConfig, index) => {
      const { label, variant, url, target, clickHandler } = buttonConfig;
      const position = (index + 1).toString();
      const normalizedLabel = label
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const occurrence = (labelOccurrences.get(normalizedLabel) || 0) + 1;
      labelOccurrences.set(normalizedLabel, occurrence);
      const testId = `pbc-cta-${normalizedLabel || 'action'}${occurrence > 1 ? `-${occurrence}` : ''}`;

      if (url) {
        if (clickHandler && url) {
          logger.debug(
            'CTA has both clickHandler and url configured. Url is prefered and will be used!',
            buttonConfig,
          );
        }

        const ctaUrlButtonProps: CtaUrlButtonProps = {
          displayname,
          label,
          variant,
          url,
          target,
          position,
          testId,
        };

        return <CtaUrlButton key={position} {...ctaUrlButtonProps} />;
      }

      if (clickHandler) {
        const ctaCallbackButtonProps: CtaCallbackButtonProps = {
          displayname,
          label,
          variant,
          clickHandler,
          position,
          testId,
        };

        return <CtaCallbackButton key={position} {...ctaCallbackButtonProps} />;
      }

      console.debug('CTA config is missing url or clickHandler!', buttonConfig);
      return undefined;
    });

    setCtaButtons(ctaBtns);
  }, [ctaBarButtonConfig, displayname, logger]);

  if (ctaBarButtonConfig.length > 0 && ctaButtons) {
    return (
      <ButtonGroup data-testid="pbc-cta-bar" margin={['var(--spacing-relative-lg)', 0, 0, 0]}>
        {ctaButtons}
      </ButtonGroup>
    );
  }
  return <div />;
};
